/**
 * @name: queryFormUtils
 * @description: xmQueryForm 公共工具函数与共享类型
 * @author: weiyong
 * @date: 2026-04-27
 */
import GoingUtils from '@/utils/goingUtils';
import { CommonEnum } from '@/main-page/utils/CommonEnum';

/**
 * 生成事件 key（pageCode + '_' + key）
 * 统一来源，避免各文件重复定义
 */
export function getEventCtlKey(key: string): string {
  return GoingUtils.getPageCode() + '_' + key;
}

/** 查询表单字段项类型 */
export interface ColumnItem {
  key?: string;
  title?: string;
  propName?: string;
  showFlag?: boolean;
  showIndex?: number;
  moreSearch?: boolean;
  controlType?: string;
  model?: { key?: string };
  props?: { label?: string; span?: string | number };
  [key: string]: unknown;
}

/** 需由控件异步解析的动态默认值标记，不能直接写入表单模型 */
const DYNAMIC_DEFAULT_TOKENS = new Set<string>([
  CommonEnum.CURRENT_DATE,
  CommonEnum.CURRENT_DATE_TIME,
  CommonEnum.CURRENT_START_TIME,
  CommonEnum.CURRENT_END_TIME,
  CommonEnum.CURRENT_TIME,
  CommonEnum.CURRENT_YEAR,
  CommonEnum.PRE_YEAR,
  CommonEnum.CURRENT_MONTH,
  CommonEnum.PRE_MONTH,
  CommonEnum.CURRENT_USER,
  CommonEnum.CURRENT_USER_ID,
  CommonEnum.CURRENT_USER_NO,
  CommonEnum.CURRENT_ORG,
  CommonEnum.CURRENT_ORG_ID,
  CommonEnum.TENANT_NAME,
  CommonEnum.TENANT_NAME_ID,
  CommonEnum.UUID,
  CommonEnum.RECENTLY_3,
  CommonEnum.RECENTLY_5,
  CommonEnum.RECENTLY_10,
  CommonEnum.RECENTLY_M_3,
  CommonEnum.RECENTLY_M_6,
  CommonEnum.RECENTLY_M_12,
  CommonEnum.RECENTLY_D_3,
  CommonEnum.RECENTLY_D_4,
  CommonEnum.RECENTLY_D_7,
  CommonEnum.RECENTLY_D_15,
  CommonEnum.RECENTLY_D_30,
  CommonEnum.WEILAI_one,
  CommonEnum.WEILAI_two,
  CommonEnum.WEILAI_three,
  CommonEnum.WEILAI_seven,
  CommonEnum.THIRTH_seven,
  'curMonthStart',
  'curMonthEnd',
  'currentTimeRang',
  'recentlyDay1',
  'session缓存',
  '全局状态变量',
]);

/** 默认值必须走控件初始化逻辑的组件（含人员/部门等异步解析） */
const ASYNC_DEFAULT_COMPONENT_KEYS = new Set([
  'UserSelect',
  'xmUserSelect',
  'DepartmentSelect',
  'xmDepartmentSelect',
]);

export function isDynamicDefaultToken(val: unknown): boolean {
  return typeof val === 'string' && DYNAMIC_DEFAULT_TOKENS.has(val);
}

/**
 * 清除表单中尚未解析的动态默认值字面量（如 currentUserId），避免直接带入查询
 */
export function sanitizeUnresolvedDynamicDefaults(
  formModel: Record<string, any> | null | undefined,
): void {
  if (!formModel || typeof formModel !== 'object') {
    return;
  }
  Object.keys(formModel).forEach((key) => {
    if (isDynamicDefaultToken(formModel[key])) {
      delete formModel[key];
    }
  });
}

/**
 * 将页面配置的【静态】字段默认值同步写入查询表单模型（仅补缺失项，不覆盖已有值）
 * 动态标记（currentUserId 等）与人员/部门选择器交给控件自身初始化，禁止写成字面量
 */
export function applyBuiltinFieldDefaultsToForm(
  formModel: Record<string, any>,
  children: any[] | undefined | null,
): void {
  if (!formModel || !Array.isArray(children)) {
    return;
  }
  children.forEach((child) => {
    if (!child?.props?.isFormEl) {
      return;
    }
    // 人员/部门等异步默认值由控件 initDefValue 解析，这里不能预写
    if (ASYNC_DEFAULT_COMPONENT_KEYS.has(child.componentKey)) {
      return;
    }

    const def = child.props?.defaultValue ?? child.dataConfig?.defValue;
    if (def == null || def === '' || (Array.isArray(def) && def.length === 0)) {
      return;
    }
    // 单个动态标记（如 currentDate）跳过
    if (!Array.isArray(def) && isDynamicDefaultToken(def)) {
      return;
    }

    let key = child.model?.key || '';
    if (!key) {
      return;
    }

    // 与 xm-form-col-item getQueryOutElementModel 保持一致：多选 Select 在查询表单下使用 _in
    const isMultiSelect =
      child.props?.multiple === true &&
      (child.componentKey === 'Select' || child.componentKey === 'xmSelect');
    if (isMultiSelect && !key.endsWith('_in') && key.indexOf('_') < 0) {
      key = `${key}_in`;
    }

    const existing = formModel[key];
    // 已有未解析动态标记时清掉，让控件有机会重新初始化
    if (isDynamicDefaultToken(existing)) {
      delete formModel[key];
    } else if (
      existing != null &&
      existing !== '' &&
      !(Array.isArray(existing) && existing.length === 0)
    ) {
      return;
    }

    // 多选与 useSelOrMoreSelVal 一致，最终以分号串参与查询
    formModel[key] = Array.isArray(def) ? def.join(';') : def;
  });
}
